import { useState, useEffect } from "react";
import { Node } from "prosemirror-model";
import BillAddComment from "../../BillAddComment/BillAddComment";
import { IProposal } from "../../../../../../types/IProposal";
import { useDispatch, useSelector } from "react-redux";
import { useFlags } from "../../../../../../config/featureFlags";
import { useAuth } from "../../../../../../utils/hooks/useAuth";
import { useAbac } from "react-abac";
import { RootState } from "../../../../../../redux/rootReducer";
import {
  proposalsStateSelector,
  proposalsSelectors,
  publishProposal,
  saveProposalState,
  setDeletableProposalId,
  setPublishableProposalId,
  voteProposalState,
  clearErrors,
} from "../../../_redux/proposalsSlice";
import { Permission } from "../../../../../../config/abac.config";
import arrowUp from "../../../../../assets/images/upArrow.svg";
import arrowdownred from "../../../../../assets/images/arrowdownred.svg";
import arrowupgreen from "../../../../../assets/images/arrowupgreen.svg";
import { billsSelector } from "../../../_redux/billsSlice";

export interface IProposalComponentProps {
  refNode: Node;
  documentId: number;
}

export const ProposalComponent = (props: IProposalComponentProps) => {
  const { refNode, documentId } = props;

  const [proposalText, setProposalText] = useState<string>("");
  const [isVisible, setVisible] = useState<boolean>(false);

  const { DISPLAY_CODE_CONTENT } = useFlags();
  const dispatch: any = useDispatch();

  const { userHasPermissions } = useAbac();

  const {
    loadingProposals,
    loadProposalsError,
    currentProposalId,
    saveProposalStateInProgress,
    savingProposalStateError,
  } = useSelector(proposalsStateSelector);
  const fetchProposalsInProgress = loadingProposals === "pending";

  let proposal: IProposal | undefined = useSelector((state: RootState) =>
    proposalsSelectors.selectById(state, refNode.attrs["data-uuid"])
  );

  const { currentBill } = useSelector(billsSelector);

  const billHasPassedDeadline =
    currentBill?.deadline && new Date(currentBill.deadline) <= new Date();

  const canViewProposal = userHasPermissions(
    Permission.VIEW_PROPOSAL,
    proposal
  );

  // For some reason api can return proposal from other team
  // We need to investigate this case later
  if (!canViewProposal) {
    proposal = undefined;
  }

  const proposalExists = !!proposal && proposal.id;
  const proposalContent = proposalExists ? proposal.content : "";

  useEffect(() => {
    setVisible(!!proposalExists);
  }, [proposalExists]);

  useEffect(() => {
    setProposalText(proposalContent);
  }, [proposalContent]);

  if (loadProposalsError) return null;

  const currentTeam = proposal?.teamName || localStorage.getItem("teamName");

  const handleAddButtonClick = async () => {
    setVisible(true);
    dispatch(clearErrors());
  };

  const handleCancelButtonClick = () => {
    setVisible(false);
  };

  const handleChangeProposalText = (e) => {
    setProposalText(e.target.value);
  };

  const handleDeleteProposal = () => {
    dispatch(setDeletableProposalId(proposal.id));
  };

  const handlePublishProposal = () => {
    dispatch(setPublishableProposalId(proposal.id));
  };

  const handleSubmitProposal = async () => {
    const teamId = localStorage.getItem("teamId");
    const data = {
      proposalId: proposal?.id,
      uuid: refNode.attrs["data-uuid"],
      refNodeJson: JSON.stringify(refNode.toJSON()),
      documentId: documentId,
      teamId: +teamId,
      content: proposalText,
    };
    await dispatch(saveProposalState(data))
      .unwrap()
      .then((data: IProposal) => {
        // In CODE SITE the proposal(a.k.a proposalText) does not need publication publish automatically
        if (DISPLAY_CODE_CONTENT) {
          dispatch(publishProposal({ proposalId: data.id, userId: null }));
        }
      });
  };

  return (
    <>
      {!isVisible && !billHasPassedDeadline && (
        <div className="addProposalButton" onClick={handleAddButtonClick} />
      )}

      {isVisible && (
        <>
          <BillAddComment
            content={proposalText}
            author={currentTeam}
            fetchProposalsInProgress={fetchProposalsInProgress}
            billHasPassedDeadline={billHasPassedDeadline}
            proposal={proposal}
            currentProposalId={currentProposalId}
            saveProposalStateInProgress={saveProposalStateInProgress}
            savingProposalStateError={savingProposalStateError}
            onChangeProposalText={handleChangeProposalText}
            onSubmitProposal={handleSubmitProposal}
            onPublishProposal={handlePublishProposal}
            onCancelButtonClick={handleCancelButtonClick}
            onDeleteProposal={handleDeleteProposal}
          />

          <ProposalVotes proposal={proposal} />
        </>
      )}
    </>
  );
};

const ProposalVotes = ({ proposal }) => {
  const { DISPLAY_CODE_CONTENT } = useFlags();
  const { userId } = useAuth();
  const dispatch: any = useDispatch();

  if (!proposal || !DISPLAY_CODE_CONTENT) return null;

  const userVote = proposal?.likes?.filter((s) => {
    return s.userId === userId;
  });

  const proposalLikes = proposal?.likes?.filter((s) => {
    return s.like === 1;
  });

  const proposalDislike = proposal?.likes?.filter((s) => {
    return s.like === -1;
  });

  const voteProposal = async (value) => {
    const proposalId = proposal.id;
    await dispatch(voteProposalState({ proposalId, value }));
  };

  return (
    <div className="voteContainer">
      <img
        src={userVote?.[0]?.like === 1 ? arrowupgreen : arrowUp}
        width={15}
        height={15}
        alt="like button"
        onClick={() => voteProposal(userVote?.[0]?.like === 1 ? 0 : 1)}
      />
      <div className="valueStyle">{proposalLikes?.length}</div>
      <img
        className={userVote?.[0]?.like !== -1 && "downArrow"}
        alt="dislike button"
        src={userVote?.[0]?.like === -1 ? arrowdownred : arrowUp}
        width={15}
        height={15}
        onClick={() => voteProposal(userVote?.[0]?.like === -1 ? 0 : -1)}
      />
      <div className="valueStyle">{proposalDislike?.length}</div>
    </div>
  );
};
